import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  formatLiksDateRangeLabel,
  type LiksDateDayRange,
} from '../../../components/liks/LiksDateRangeDialog/dateRangePicker';
import { useI18n } from '../../../i18n/useI18n';
import type { LacTenantOverviewDateRange } from '../../../types/lac';
import { dayToIsoRange } from '../../../utils/dateTimeUtils';
import {
  ALL_TENANTS_FILTER,
  buildLacDocumentsSearchParams,
  parseLacDocumentsFilterFromSearchParams,
  tenantOptionLabel,
} from '../lacDocumentsListParams';

type LacTenantOption = {
  name: string;
  displayName?: string | null;
};

type UseLacDocumentsSearchParamsOptions = {
  showTenantFilter: boolean;
  tenants: LacTenantOption[] | undefined;
};

export function useLacDocumentsSearchParams({
  showTenantFilter,
  tenants,
}: UseLacDocumentsSearchParamsOptions) {
  const { locale, translate } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDateDialogOpen, setIsDateDialogOpen] = useState(false);

  const filter = useMemo(
    () =>
      parseLacDocumentsFilterFromSearchParams(searchParams, {
        includeTenant: showTenantFilter,
      }),
    [searchParams, showTenantFilter],
  );

  const replaceParams = useCallback(
    ({
      tenant,
      fromDay,
      toDay,
    }: {
      tenant: string;
      fromDay: string;
      toDay: string;
    }) => {
      setSearchParams(
        buildLacDocumentsSearchParams({
          tenant,
          fromDay,
          toDay,
          includeTenant: showTenantFilter,
        }),
        { replace: true },
      );
    },
    [setSearchParams, showTenantFilter],
  );

  const handleTenantFilterChange = useCallback(
    (value: string) => {
      replaceParams({
        tenant: value,
        fromDay: filter.fromDay,
        toDay: filter.toDay,
      });
    },
    [filter.fromDay, filter.toDay, replaceParams],
  );

  const handleApplyDateRange = useCallback(
    (range: LiksDateDayRange | null) => {
      replaceParams({
        tenant: filter.tenant,
        fromDay: range?.fromDay ?? '',
        toDay: range?.toDay ?? '',
      });
      setIsDateDialogOpen(false);
    },
    [filter.tenant, replaceParams],
  );

  const selectedDayRange = useMemo((): LiksDateDayRange | null => {
    if (
      filter.fromDay === '' ||
      filter.toDay === '' ||
      filter.toDay < filter.fromDay
    ) {
      return null;
    }
    return { fromDay: filter.fromDay, toDay: filter.toDay };
  }, [filter.fromDay, filter.toDay]);

  const dateRange = useMemo((): LacTenantOverviewDateRange | undefined => {
    if (selectedDayRange == null) {
      return undefined;
    }
    return dayToIsoRange(selectedDayRange.fromDay, selectedDayRange.toDay);
  }, [selectedDayRange]);

  const tenantFilterOptions = useMemo(() => {
    const sortedTenants = [...(tenants ?? [])].sort((left, right) =>
      tenantOptionLabel(left).localeCompare(tenantOptionLabel(right), locale, {
        sensitivity: 'base',
      }),
    );

    return [
      {
        value: ALL_TENANTS_FILTER,
        label: translate('lac.documents.tenantFilter.all'),
      },
      ...sortedTenants.map((tenant) => ({
        value: tenant.name,
        label: tenantOptionLabel(tenant),
      })),
    ];
  }, [locale, tenants, translate]);

  const rangeLabel = formatLiksDateRangeLabel(
    selectedDayRange,
    locale,
    translate('common.dateRange.entireTime'),
  );

  const hasActiveTenantFilter =
    showTenantFilter && filter.tenant !== ALL_TENANTS_FILTER;

  return {
    tenantFilter: filter.tenant,
    handleTenantFilterChange,
    tenantFilterOptions,
    fromDay: filter.fromDay,
    toDay: filter.toDay,
    isDateDialogOpen,
    setIsDateDialogOpen,
    handleApplyDateRange,
    rangeLabel,
    dateRange,
    hasActiveTenantFilter,
  };
}
